/* eslint-disable react-hooks/exhaustive-deps */
import styles from './TrelloBoard.module.scss';
import React, { useCallback, useEffect } from 'react';
import { useContext } from 'react';
import Spinner from '@atlaskit/spinner';
import { TrelloPowerUpContext } from '@powerupsclub/trello';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3001';

interface BoardElement extends HTMLElement {
  board: any;
}

interface CardElement extends Element {
  card?: any;
}

interface TrelloBoardProps {
  boardData: any;
  loading: boolean;
  error?: Error;
}

async function sendListToBoard(
  idList: string,
  jwt: string,
  withAllCards: boolean
) {
  const repsonse = await fetch(
    `${SERVER_URL}/archive-manager/send-list-to-board/${idList}`,
    {
      method: 'post',
      body: JSON.stringify({
        jwt,
        withAllCards,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  return repsonse.json();
}

async function deleteAllCardsInList(
  idList: string,
  jwt: string,
  allCards: boolean
) {
  const repsonse = await fetch(
    `${SERVER_URL}/archive-manager/delete-cards-in-list/${idList}?jwt=${jwt}${
      allCards ? '&allCards=true' : ''
    }`,
    {
      method: 'delete',
    }
  );

  return repsonse.json();
}

export const TrelloBoard: React.FC<TrelloBoardProps> = ({
  boardData,
  loading,
  error,
}) => {
  const { trelloIframe, token } = useContext(TrelloPowerUpContext);

  useEffect(() => {
    const renderBoard = async () => {
      const boardContent = document.getElementById('trello-board-content');

      if (!boardContent) {
        return;
      }

      if (boardData == null) {
        return;
      }
      boardContent.innerHTML = '';
      const boardEl = document.createElement('trello-board') as BoardElement;
      boardEl.board = boardData;
      boardContent.appendChild(boardEl);

      const trelloBoardShadowDom =
        document.querySelector('trello-board')?.shadowRoot;

      trelloBoardShadowDom
        ?.querySelectorAll('trello-card')
        .forEach((trelloCard: CardElement) => {
          const a = trelloCard.shadowRoot?.querySelector('a.card');
          const { card } = trelloCard;

          if (a) {
            a.removeAttribute('target');
            a.setAttribute('href', '#');

            a.addEventListener('click', (e) => {
              e.preventDefault();
              const selectCardEvent = new CustomEvent('select-card', {
                detail: {
                  card,
                  e,
                },
              });
              window.dispatchEvent(selectCardEvent);
            });
          }
        });

      trelloBoardShadowDom
        ?.querySelectorAll('.list')
        .forEach((listElement: any, index: number) => {
          const listData = boardData.lists[index];

          const listButton = document.createElement('a');
          listButton.text = '...';

          listButton.style.position = 'absolute';
          listButton.style.top = '8px';
          listButton.style.right = '8px';
          listButton.style.borderRadius = '3px';
          // listButton.style.backgroundColor = 'red';
          listButton.style.height = '24px';
          listButton.style.width = '24px';
          listButton.style.textAlign = 'center';
          listButton.style.color = '#172B4D';
          listButton.style.cursor = 'pointer';

          listButton.onmouseenter = () => {
            listButton.style.backgroundColor = '#091e4214';
          };

          listButton.onmouseleave = () => {
            listButton.style.backgroundColor = '';
          };

          listButton.onclick = (e) => {
            const selectListEvent = new CustomEvent('select-list', {
              detail: {
                list: listData,
                e,
              },
            });
            window.dispatchEvent(selectListEvent);
          };

          listElement.appendChild(listButton);
        });

      const trelloBoardHeader =
        trelloBoardShadowDom?.querySelector('.board-header');
      trelloBoardHeader?.remove();

      const boardHeaderButton = trelloBoardShadowDom?.querySelector(
        '.board-header-button'
      );
      boardHeaderButton?.remove();

      try {
        const boardContainer: any =
          trelloBoardShadowDom?.querySelector('.board');
        boardContainer.style['padding-top'] = '44px';
      } catch (e) {}
    };

    renderBoard();
  }, [boardData]);

  const onCardSelect = useCallback((event: any) => {
    const { card, e } = event.detail;
    trelloIframe.popup({
      mouseEvent: e,
      title: card.name,
      items: [
        {
          text: 'Open card',
          callback: (x: any) => {
            x.navigate({
              url: card.shortUrl,
            });
          },
        },
        {
          text: 'Send card to board...',
          callback: (x: any) => {
            x.popup({
              title: card.name,
              url: './send-to-board',
              args: { idCard: card.id },
              height: 148,
            });
          },
        },
        {
          text: 'Delete card...',
          callback: (x: any) => {
            x.popup({
              type: 'confirm',
              title: 'Delete card?',
              message:
                'This will permanently delete this card from Trello. There is no undo. Are you sure?',
              confirmText: 'Delete',
              confirmStyle: 'danger',
              onConfirm: async (x: any) => {
                x.closePopup();
                const jwt = await x.jwt({ state: JSON.stringify({ token }) });
                await fetch(
                  `${SERVER_URL}/archive-manager/delete-card/${card.id}?jwt=${jwt}`,
                  {
                    method: 'delete',
                  }
                );

                await trelloIframe.set(
                  'board',
                  'private',
                  'lastLoadTime',
                  new Date().getTime()
                );

                trelloIframe.alert({
                  message: 'Card was deleted',
                });
              },
            });
          },
        },
      ],
    });
  }, []);

  useEffect(() => {
    window.addEventListener('select-card', onCardSelect);

    return () => window.removeEventListener('select-card', onCardSelect);
  }, [onCardSelect]);

  const onListSelect = useCallback((event: any) => {
    const { list, e } = event.detail;
    trelloIframe.popup({
      mouseEvent: e,
      title: list.name,
      items: [
        ...(list.closed
          ? [
              {
                text: 'Send list to board',
                callback: async (x: any) => {
                  x.closePopup();
                  const jwt = await x.jwt({ state: JSON.stringify({ token }) });
                  await sendListToBoard(list.id, jwt, false);

                  await trelloIframe.set(
                    'board',
                    'private',
                    'lastLoadTime',
                    new Date().getTime()
                  );

                  trelloIframe.alert({
                    message: 'List was sent back to the board',
                  });
                },
              },
            ]
          : []),
        ...(list.closed || boardData.idListsWithArchivedCards.includes(list.id)
          ? [
              {
                text: list.closed
                  ? 'Send list to board with all cards'
                  : 'Send all cards to board',
                callback: async (x: any) => {
                  x.closePopup();
                  trelloIframe.alert({
                    message:
                      'Sending list with all cards back to the board. This might take sometime...',
                  });

                  const jwt = await x.jwt({ state: JSON.stringify({ token }) });
                  await sendListToBoard(list.id, jwt, true);

                  await trelloIframe.set(
                    'board',
                    'private',
                    'lastLoadTime',
                    new Date().getTime()
                  );

                  trelloIframe.alert({
                    message: 'List with all cards was sent back to the board',
                  });
                },
              },
            ]
          : []),
        {
          text: 'Delete all cards in list...',
          callback: (x: any) => {
            function deleteAllClosedCards(y: any) {
              y.popup({
                type: 'confirm',
                title: 'Delete all archived cards?',
                message:
                  'This will permanently delete these cards from Trello. There is no undo. Are you sure?',
                confirmText: 'Delete',
                confirmStyle: 'danger',
                onConfirm: async (x: any) => {
                  x.closePopup();
                  trelloIframe.alert({
                    message: `Deleting all the archived cards in the list ${list.name}. This might take some time...`,
                  });
                  const jwt = await x.jwt({
                    state: JSON.stringify({ token }),
                  });
                  await deleteAllCardsInList(list.id, jwt, false);
                  await trelloIframe.set(
                    'board',
                    'private',
                    'lastLoadTime',
                    new Date().getTime()
                  );
                  trelloIframe.alert({
                    message: `Deleted all the archived cards in the list ${list.name}`,
                  });
                },
              });
            }

            if (list.closed) {
              x.popup({
                title: 'Delete all cards...',
                items: [
                  {
                    text: 'All cards (including open cards)',
                    callback: (x: any) => {
                      x.popup({
                        type: 'confirm',
                        title: 'Delete all cards in this list?',
                        message:
                          'This will permanently delete these cards from Trello. There is no undo. Are you sure?',
                        confirmText: 'Delete',
                        confirmStyle: 'danger',
                        onConfirm: async (x: any) => {
                          x.closePopup();
                          trelloIframe.alert({
                            message: `Deleting all the cards in the list ${list.name}. This might take some time...`,
                          });
                          const jwt = await x.jwt({
                            state: JSON.stringify({ token }),
                          });
                          await deleteAllCardsInList(list.id, jwt, true);
                          await trelloIframe.set(
                            'board',
                            'private',
                            'lastLoadTime',
                            new Date().getTime()
                          );
                          trelloIframe.alert({
                            message: `Deleted all the cards in the list ${list.name}`,
                          });
                        },
                      });
                    },
                  },
                  {
                    text: 'Archived cards',
                    callback: (x: any) => {
                      deleteAllClosedCards(x);
                    },
                  },
                ],
              });
            } else {
              deleteAllClosedCards(x);
            }
          },
        },
      ],
    });
  }, []);

  useEffect(() => {
    window.addEventListener('select-list', onListSelect);

    return () => window.removeEventListener('select-list', onListSelect);
  }, [onCardSelect]);

  if (error) {
    return (
      <div className={styles.errorState}>
        <div>
          <h1>🙁 Unable to load this board.</h1>

          <p>
            Contact the person who shared this link with you and ask them for a
            new one.
          </p>

          <div className={styles.footer}>
            <a href="https://trello.com/power-ups">External Share</a> by{' '}
            <a href="https://powerups.club">Power-Ups Club</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div id="trello-board-content" className={styles.boardContainer} />
      {loading && (
        <div className={styles.loadingContainer}>
          <Spinner size="large" />
        </div>
      )}
    </>
  );
};
